import * as React from 'react';
import { ActionGroup, Alert, Button, Checkbox, Form, FormAlert, FormGroup, FormHelperText, TextInput, Title } from '@patternfly/react-core';
import { ExclamationCircleIcon } from '@patternfly/react-icons';
import ValidatedType from '@app/Type/ValidatedType';
import TypeAssertions from '@app/utils/TypeAssertions';
import FormAlertInterface from '@app/Interface/FormAlertInterface';
import TwoFactorHelper from '@app/Service/TwoFactorHelper';
import ItemLoader from '../ItemLoader';
import UserAuthentication from '@app/Security/UserAuthentication';
import User from '@app/Interface/UserInterface';
import Cookies from 'js-cookie';
import moment from "moment-timezone";

interface User2FALoginFormProps {
    email: string;
    handleLogin?: (user: User) => void;
}

const User2FALoginForm: React.FC<User2FALoginFormProps> = ({ email, handleLogin }) => {

    const [twoFactorCode, setTwoFactorCode] = React.useState("");

    const [sendingCode, setSendingCode] = React.useState(false);

    const [validCode, setValidCode] = React.useState<ValidatedType>("default");

    const [rememberDevice, setRememberDevice] = React.useState(false);

    const [isFormProcessing, setIsFormProcessing] = React.useState(false);

    const [formValidated, setFormValidated] = React.useState<ValidatedType>("default");

    const [formAlertMessage, setFormAlertMessage] = React.useState<FormAlertInterface>({ variant: "default", title: "", isInline: true });

    const [codeValidated, setCodeValidated] = React.useState(false);

    const [loggingIn, setLoggingIn] = React.useState(false);

    const [disableResendButton, setDisableResendButton] = React.useState(false);

    const handleSubmit = async (event: React.SyntheticEvent) => {

        event.preventDefault();

        setIsFormProcessing(true);

        setDisableResendButton(true);

        setValidCode("default");

        setFormValidated("default");

        if (TypeAssertions.isStringNullOrEmpty(twoFactorCode)) {

            setValidCode("error");

        } else {

            const [codeValidated, errorMessage] = await TwoFactorHelper.verifyCode(twoFactorCode);

            if (codeValidated) {

                if (rememberDevice) {

                    const expireDate = moment().add(30, "days");

                    Cookies.set("2fa-remember-device", expireDate.format(), { expires: 30, secure: true, sameSite: "strict" });

                }
                
                setCodeValidated(true);

            } else {

                setFormValidated("error");

                setFormAlertMessage({ variant: "danger", title: errorMessage, isInline: true });

            }
        }

        setIsFormProcessing(false);

        setDisableResendButton(false);

    };

    const handleResendCode = async () => {

        const controller = new AbortController();

        let mounted = true;

        async function sendCode() {

            // send the code
            const [sent, errorMessage] = await TwoFactorHelper.sendTwoFactorCode(controller);

            if (mounted) {

                setTwoFactorCode("");

                if (sent) {

                    setFormValidated("success");

                    setFormAlertMessage({ variant: "success", title: "A code was successfully sent.", isInline: true });

                } else {

                    setFormValidated("error");

                    setFormAlertMessage({ variant: "danger", title: errorMessage, isInline: true });

                }

                setSendingCode(false);
            }

        }

        setSendingCode(true);

        sendCode();

        return () => {
            mounted = false;
            controller?.abort();
        };

    }

    // on mount, send code to user
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        async function sendCode() {

            // send the code
            const [sent, errorMessage] = await TwoFactorHelper.sendTwoFactorCode(controller);

            if (mounted) {
                if (sent) {

                    setFormValidated("success");

                    setFormAlertMessage({ variant: "success", title: "A code was successfully sent.", isInline: true });

                } else {

                    setFormValidated("error");

                    setFormAlertMessage({ variant: "danger", title: errorMessage, isInline: true });

                }

                setSendingCode(false);
            }

        }

        setSendingCode(true);

        sendCode();

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, []);

    // if validated code, log the user in
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        async function login() {

            const user = await UserAuthentication.getUserByEmailAddress(controller, email);

            if (mounted) {
                if (handleLogin && typeof user !== "boolean") {
                    handleLogin(user);
                } else {
                    console.warn("no handleLogin function was passed!");
                }

                setLoggingIn(false);
            }

        }

        if (codeValidated) {

            setLoggingIn(true);

            login();

        }

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [codeValidated, email, handleLogin]);

    if (loggingIn) {

        return (
            <>
                <Title headingLevel="h2">Logging In!</Title>
                <ItemLoader />
            </>
        );

    }

    if (sendingCode) {
        return (
            <>
                <Title headingLevel="h2">Sending code</Title>
                <ItemLoader />
            </>
        );
    }

    return (
        <Form autoComplete="false" onSubmit={handleSubmit}>
            {formValidated !== "default" && (
                <FormAlert>
                    <Alert
                        variant={formAlertMessage.variant}
                        title={formAlertMessage.title}
                        aria-live="polite"
                        isInline={formAlertMessage.isInline}
                        role="alert"
                    ></Alert>
                </FormAlert>
            )}
            <FormGroup
                label="Enter Your Code"
                type="string"
                helperText={
                    // eslint-disable-next-line react/jsx-no-undef
                    <FormHelperText icon={<ExclamationCircleIcon />} isHidden={validCode !== "error"}>Please enter a code.</FormHelperText>
                }
                helperTextInvalid="Enter the code"
                helperTextInvalidIcon={<ExclamationCircleIcon />}
                fieldId="twoFactorCode"
                validated={validCode}
            >
                <TextInput
                    validated={validCode}
                    value={twoFactorCode}
                    id="twoFactorCode"
                    aria-describedby="twoFactorCode"
                    onChange={(newValue: string) => setTwoFactorCode(newValue)}
                    autoComplete="one-time-code"
                    inputMode="numeric"
                />
            </FormGroup>
            <FormGroup
                label=""
                type="checkbox"
                fieldId="rememberDevice"
            >
                <Checkbox
                    label="Remember device for 30 days?"
                    name="rememberDevice"
                    aria-label="Remember device for 30 days?"
                    isChecked={rememberDevice}
                    onChange={() => setRememberDevice(!rememberDevice)}
                    value="1"
                    id="rememberDevice"
                />
            </FormGroup>
            <ActionGroup>
                <Button variant="primary" onClick={handleSubmit} isLoading={isFormProcessing}>
                    {isFormProcessing ? "Sending" : "Submit"}
                </Button>
                <Button variant="link" onClick={handleResendCode} disabled={disableResendButton}>
                    {sendingCode ? "Sending" : "Resend Code"}
                </Button>

            </ActionGroup>
        </Form>
    );

}

export default User2FALoginForm;
